import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload, formatImageUrl, cleanUrl, stripHtml } from '../../utils/security';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminBlogEdit = () => {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const { user, isBlogManager, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);

    const currentAdminLang = i18n.language === 'en' ? 'en' : 'ka';
    const [currentId, setCurrentId] = useState(id || null);

    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        author: 'Orbitronix Team',
        date: new Date().toLocaleDateString('ka-GE', { month: 'long', day: 'numeric', year: 'numeric' }),
        category: 'tech',
        summary: '',
        content: '',
        image: '',
        seo_title: '',
        seo_description: '',
        seo_keywords: '',
        lang: currentAdminLang
    });

    useEffect(() => {
        if (!authLoading && !(isSuperAdmin() || isBlogManager())) navigate('/admin');
    }, [authLoading, isSuperAdmin, isBlogManager, navigate]);

    // Fetch dynamic categories
    useEffect(() => {
        const fetchCats = async () => {
            try {
                const res = await fetchWithSecurity(`${API_URL}categories.php?type=blog`);
                const data = await res.json();
                if (Array.isArray(data) && data.length > 0) setCategories(data);
            } catch (err) {
                console.error('Error fetching blog categories:', err);
            }
        };
        fetchCats();
    }, []);

    useEffect(() => {
        if (user) {
            const fetchBlog = async () => {
                if (id) {
                    try {
                        const response = await fetchWithSecurity(`${API_URL}blogs.php?id=${id}`);
                        const data = await response.json();
                        if (data) {
                            // If current admin language is different from item's language, check if translated counterpart exists
                            if (data.lang !== currentAdminLang && data.slug) {
                                try {
                                    const matchRes = await fetchWithSecurity(`${API_URL}blogs.php?slug=${data.slug}&lang=${currentAdminLang}`);
                                    const matchData = await matchRes.json();
                                    if (matchData && matchData.id) {
                                        setCurrentId(matchData.id);
                                        setFormData({
                                            ...matchData,
                                            category: matchData.category || 'tech',
                                            seo_title: matchData.seo_title || '',
                                            seo_description: matchData.seo_description || '',
                                            seo_keywords: matchData.seo_keywords || '',
                                            lang: currentAdminLang
                                        });
                                        return;
                                    }
                                } catch (e) {
                                    // fallback
                                }
                            }

                            setCurrentId(data.id);
                            setFormData({
                                ...data,
                                category: data.category || 'tech',
                                seo_title: data.seo_title || '',
                                seo_description: data.seo_description || '',
                                seo_keywords: data.seo_keywords || '',
                                lang: data.lang || currentAdminLang
                            });
                        }
                    } catch (error) {
                        console.error('Error fetching blog:', error);
                    }
                } else {
                    setFormData(prev => ({ ...prev, lang: currentAdminLang }));
                }
            };
            fetchBlog();
        }
    }, [id, user, currentAdminLang]);

    const handleSwitchLanguage = async (targetLang) => {
        if (formData.lang === targetLang) return;

        if (formData.slug) {
            setLoading(true);
            try {
                const res = await fetchWithSecurity(`${API_URL}blogs.php?slug=${formData.slug}&lang=${targetLang}`);
                const match = await res.json();
                if (match && match.id) {
                    setCurrentId(match.id);
                    setFormData({
                        ...match,
                        category: match.category || 'tech',
                        seo_title: match.seo_title || '',
                        seo_description: match.seo_description || '',
                        seo_keywords: match.seo_keywords || '',
                        lang: targetLang
                    });
                    toast.info(targetLang === 'ka' ? 'გადართულია ქართულ ვერსიაზე' : 'Switched to English version');
                    setLoading(false);
                    return;
                }
            } catch (err) {
                console.error(err);
            }
            setLoading(false);
        }

        setCurrentId(null);
        setFormData(prev => ({
            ...prev,
            lang: targetLang,
            title: '',
            summary: '',
            content: '',
            seo_title: '',
            seo_description: ''
        }));
        toast.info(targetLang === 'ka'
            ? 'ქართული ვერსია ჯერ არ არსებობს. შეგიძლიათ შეავსოთ და გამოაქვეყნოთ.'
            : 'English version does not exist yet. You can fill out and publish it.'
        );
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleQuillChange = (value) => {
        setFormData(prev => ({ ...prev, content: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const data = new FormData();
        data.append('image', file);
        try {
            const res = await fetchWithSecurity(`${API_URL}upload.php`, { method: 'POST', body: data });
            const result = await res.json();
            if (result.success) {
                setFormData(prev => ({ ...prev, image: cleanUrl(result.url) }));
                toast.success('სურათი აიტვირთა');
            } else {
                toast.error('სურათის ატვირთვა ვერ მოხერხდა: ' + result.error);
            }
        } catch (error) {
            toast.error('სურათის ატვირთვა ვერ მოხერხდა');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const sanitizedForm = sanitizePayload({
                ...formData,
                image: cleanUrl(formData.image),
                summary: stripHtml(formData.summary)
            });
            const response = await fetchWithSecurity(`${API_URL}blogs.php${currentId ? `?id=${currentId}` : ''}`, {
                method: currentId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(currentId ? { ...sanitizedForm, id: currentId } : sanitizedForm)
            });
            const result = await response.json();
            if (result.success) {
                toast.success('სტატია წარმატებით გამოქვეყნდა!');
                navigate('/admin/blogs');
            } else {
                toast.error('შეცდომა: ' + (result.error || 'სტატიის შენახვა ვერ მოხერხდა'));
            }
        } catch (error) {
            toast.error('სტატიის შენახვა ვერ მოხერხდა');
        }
        setLoading(false);
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image', 'code-block'],
            ['clean']
        ],
    };

    if (authLoading || !user) return null;

    const pageTitle = currentId ? 'სტატიის რედაქტირება' : 'ახალი სტატია';

    return (
        <AdminLayout title={pageTitle}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button className="shadcn-btn shadcn-btn-secondary" onClick={() => navigate('/admin/blogs')}>
                    <ArrowLeft size={16} /> {t('common.back') || 'სტატიებში დაბრუნება'}
                </button>
                <button className="shadcn-btn shadcn-btn-primary" onClick={handleSubmit} disabled={loading}>
                    <Save size={16} /> {loading ? 'ინახება...' : 'სტატიის გამოქვეყნება'}
                </button>
            </div>

            <div className="shadcn-card max-w-4xl mx-auto">
                {/* ── PROMINENT LANGUAGE SWITCHER BAR WITH FLAGS ── */}
                <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border mb-4 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold small text-dark">
                            {formData.lang === 'ka' ? '🇬🇪 რედაქტირების ენა:' : '🇬🇧 Active Editing Language:'}
                        </span>
                        <span className="shadcn-badge shadcn-badge-primary">
                            {formData.lang === 'ka' ? '🇬🇪 ქართული (Georgian)' : '🇬🇧 ინგლისური (English)'}
                        </span>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className={`shadcn-btn shadcn-btn-sm ${formData.lang === 'ka' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => handleSwitchLanguage('ka')}
                        >
                            <span style={{ fontSize: '1.15rem', marginRight: '4px' }}>🇬🇪</span> ქართული ვერსია
                        </button>
                        <button
                            type="button"
                            className={`shadcn-btn shadcn-btn-sm ${formData.lang === 'en' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => handleSwitchLanguage('en')}
                        >
                            <span style={{ fontSize: '1.15rem', marginRight: '4px' }}>🇬🇧</span> English Version
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <label className="form-group-label">{t('admin.blogEdit.language') || 'ენა'}</label>
                            <select 
                                name="lang" 
                                value={formData.lang} 
                                onChange={e => handleSwitchLanguage(e.target.value)} 
                                className="shadcn-select"
                            >
                                <option value="ka">🇬🇪 ქართული</option>
                                <option value="en">🇬🇧 English</option>
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-group-label">კატეგორია</label>
                            <select 
                                name="category" 
                                value={formData.category} 
                                onChange={handleInputChange} 
                                className="shadcn-select"
                            >
                                {categories.length > 0 ? (
                                    categories.map(c => (
                                        <option key={c.slug} value={c.slug}>
                                            {formData.lang === 'ka' ? c.name_ka : (c.name_en || c.name_ka)}
                                        </option>
                                    ))
                                ) : (
                                    <>
                                        <option value="tech">ტექნოლოგიები</option>
                                        <option value="design">დიზაინი & UI/UX</option>
                                        <option value="cybersecurity">კიბერუსაფრთხოება</option>
                                        <option value="business">ბიზნესი</option>
                                        <option value="ai">AI</option>
                                    </>
                                )}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-group-label">{t('admin.blogEdit.author') || 'ავტორი'}</label>
                            <input type="text" name="author" value={formData.author} onChange={handleInputChange} className="shadcn-input" />
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.blogEdit.title') || 'სტატიის სათაური'}</label>
                            <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="shadcn-input" required />
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.blogEdit.slug') || 'URL იდენტიფიკატორი (Slug)'}</label>
                            <input type="text" name="slug" value={formData.slug} onChange={handleInputChange} className="shadcn-input" required />
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.blogEdit.featuredImage') || 'მთავარი ქავერ სურათი'}</label>
                            <div className="d-flex gap-4 align-items-center p-3 rounded-3 bg-light border">
                                {formData.image && (
                                    <img src={formatImageUrl(formData.image)} alt="Preview" className="rounded-3 object-fit-cover shadow-sm" style={{ width: '100px', height: '70px' }} />
                                )}
                                <div className="flex-grow-1">
                                    <input type="file" onChange={handleImageUpload} className="form-control mb-2" accept="image/*" />
                                    <input type="text" name="image" value={formData.image} onChange={handleInputChange} placeholder="ან ჩასვით სურათის URL" className="shadcn-input" />
                                </div>
                            </div>
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.blogEdit.summary') || 'მოკლე მიმოხილვა'}</label>
                            <textarea name="summary" value={formData.summary} onChange={handleInputChange} rows="3" className="shadcn-input" placeholder="მოკლე ტექსტი ბარათისთვის..." required></textarea>
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.blogEdit.content') || 'სტატიის სრული ტექსტი'}</label>
                            <div className="quill-wrapper">
                                <ReactQuill value={formData.content} onChange={handleQuillChange} modules={quillModules} className="bg-white rounded-3 overflow-hidden border" />
                            </div>
                        </div>

                        {/* SEO Section */}
                        <div className="col-md-12">
                            <h5 className="fw-bold text-dark mt-4 mb-3 d-flex align-items-center gap-2">
                                <Sparkles size={18} className="text-primary" /> {t('admin.blogEdit.seoSettings') || 'საძიებო ოპტიმიზაცია (SEO)'}
                            </h5>
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.blogEdit.seoTitle') || 'SEO სათაური'}</label>
                            <input type="text" name="seo_title" value={formData.seo_title} onChange={handleInputChange} placeholder="Meta Title (მაქს. 60 სიმბოლო)" className="shadcn-input" />
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.blogEdit.seoDescription') || 'SEO აღწერა'}</label>
                            <textarea name="seo_description" value={formData.seo_description} onChange={handleInputChange} rows="2" placeholder="Meta Description (მაქს. 160 სიმბოლო)" className="shadcn-input"></textarea>
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.blogEdit.seoKeywords') || 'საკვანძო სიტყვები'}</label>
                            <input type="text" name="seo_keywords" value={formData.seo_keywords} onChange={handleInputChange} placeholder="მაგ. web, design, technology" className="shadcn-input" />
                        </div>
                    </div>

                    <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                        <button type="button" className="shadcn-btn shadcn-btn-secondary" onClick={() => navigate('/admin/blogs')}>
                            {t('common.cancel') || 'გაუქმება'}
                        </button>
                        <button type="submit" className="shadcn-btn shadcn-btn-primary" disabled={loading}>
                            <Save size={16} /> {loading ? 'ინახება...' : 'სტატიის გამოქვეყნება'}
                        </button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};

export default AdminBlogEdit;
